import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Button,
  Card,
  Icon,
  MOODS,
  MoodDot,
  OwlMark,
  StreakPill,
  cn,
  type MoodId,
} from "@wiseowl/design-system";
import { useAuth } from "../lib/auth-context.js";
import { api } from "../lib/api.js";
import { isApiError, type DailyGreeting } from "@wiseowl/shared";
import type { AppShellContext } from "../layouts/app-layout.js";

interface ToolCardProps {
  icon: React.ReactNode;
  title: string;
  sub: string;
  featured?: boolean;
  onClick?: () => void;
}

const greetingFor = (d = new Date()) => {
  const hour = d.getHours();
  const day = d.toLocaleDateString("en-US", { weekday: "long" });
  const slot = hour < 5 ? "night" : hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  return `${day} ${slot}`;
};

/**
 * Home — the Compass core view.
 *
 * Greeting + streak → mood arrival row → Today's Reset hero → tools
 * grid → daily wisdom. Layout follows the brand reference 1:1; copy
 * keeps the wise-friend voice (no exclamation marks, no emoji,
 * sentence case throughout).
 */
export const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openReflect } = useOutletContext<AppShellContext>();
  const [mood, setMood] = useState<MoodId | null>(null);
  const [streakDays, setStreakDays] = useState<number>(0);
  const [greeting, setGreeting] = useState<DailyGreeting | null>(null);
  const firstName = user?.name?.split(" ")[0] ?? "friend";

  // Pull the live streak + most-recent mood + today's greeting on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [s, m, g] = await Promise.all([
        api<{ streak: { currentDays: number } }>("/streak?practice=daily-reset"),
        api<{ moods: { moodSlug: string }[] }>("/moods?limit=1"),
        api<{ greeting: DailyGreeting }>(`/daily-greeting?h=${new Date().getHours()}`),
      ]);
      if (cancelled) return;
      if (!isApiError(s)) setStreakDays(s.data.streak.currentDays);
      if (!isApiError(m) && m.data.moods[0]) setMood(m.data.moods[0].moodSlug as MoodId);
      if (!isApiError(g)) setGreeting(g.data.greeting);
    })();
    return () => { cancelled = true; };
  }, []);

  const pickMood = (id: MoodId) => {
    setMood(id);
    // Fire-and-forget — UI updates optimistically; server records the pick.
    void api("/moods", { method: "POST", body: JSON.stringify({ moodSlug: id, source: "home-row" }) });
  };

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-small font-medium text-muted">{greetingFor()}</div>
          <h1 className="mt-1 font-serif text-display font-medium leading-tight tracking-tight text-ink">
            {greeting?.greeting ?? `Hello, ${firstName}.`}
          </h1>
          {greeting?.note && (
            <p className="mt-1 text-small text-muted">{greeting.note}</p>
          )}
        </div>
        <StreakPill days={streakDays} />
      </header>

      {/* Mood row */}
      <section>
        <div className="mb-3 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
          How are you arriving?
        </div>
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {MOODS.map((m) => (
            <MoodDot
              key={m.id}
              mood={m}
              active={mood === m.id}
              onClick={() => pickMood(m.id)}
            />
          ))}
        </div>
      </section>

      {/* Today's Reset hero */}
      <section>
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-teal to-teal-2 p-6 text-cream">
          {/* Mustard sun in the corner */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-12 -top-12 h-52 w-52 rounded-pill opacity-55"
            style={{ background: "radial-gradient(circle, rgb(var(--wo-mustard)) 0%, transparent 70%)" }}
          />
          <div className="relative flex items-center gap-2">
            <Icon name="spark" size={18} className="text-mustard" />
            <span className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-mustard">
              Today&apos;s 3-minute reset
            </span>
          </div>
          <p className="relative mt-3.5 font-serif text-[clamp(20px,4vw,28px)] font-medium leading-snug tracking-tight text-pretty">
            {greeting?.prompt ?? "What are you holding onto that is quietly draining your energy?"}
          </p>
          <div className="relative mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-3.5 text-meta text-cream/80">
              <span>1 · Breathe</span>
              <span>2 · Reflect</span>
              <span>3 · Reset</span>
            </div>
            <Button
              variant="mustard"
              onClick={openReflect}
              trailingIcon={<Icon name="arrow" size={16} />}
            >
              Begin
            </Button>
          </div>
        </div>
      </section>

      {/* Tools grid */}
      <section>
        <div className="mb-2.5 flex items-baseline justify-between">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
            Tools
          </div>
          <button
            onClick={() => navigate("/reflect")}
            className="font-mono text-eyebrow uppercase tracking-wider text-teal"
          >
            See all
          </button>
        </div>
        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
          <ToolCard
            featured
            icon={<OwlMark size={36} bgClassName="bg-mustard" />}
            title="Talk it through"
            sub="A wise, private conversation"
            onClick={() => navigate("/companion")}
          />
          <ToolCard
            icon={<Icon name="book" size={22} className="text-teal" />}
            title="Free journal"
            sub="Write & reflect"
            onClick={openReflect}
          />
          <ToolCard
            icon={<Icon name="leaf" size={22} className="text-teal" />}
            title="Breathe"
            sub="60-second reset"
            onClick={openReflect}
          />
          <ToolCard
            icon={<Icon name="chart" size={22} className="text-teal" />}
            title="Patterns"
            sub="This week"
            onClick={() => navigate("/library")}
          />
          <ToolCard
            icon={<Icon name="bookmark" size={22} className="text-teal" />}
            title="Saved"
            sub="Your reflections"
            onClick={() => navigate("/library")}
          />
        </div>
      </section>

      {/* Daily wisdom */}
      <section>
        <div className="mb-2.5 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
          A quiet thought, for today
        </div>
        <Card surface="cream-2" className="relative">
          <span
            aria-hidden="true"
            className="absolute right-5 top-3.5 select-none font-serif text-[56px] leading-[0.8] text-mustard/40"
          >
            &ldquo;
          </span>
          <p className="pr-7 font-serif text-h3 italic leading-relaxed text-ink text-pretty">
            {greeting?.wisdom ?? "Clarity begins when the mind becomes quiet. The owl waits for stillness before it speaks."}
          </p>
          <div className="mt-3 text-meta text-muted">— Wise Owl Talks, daily wisdom</div>
        </Card>
      </section>
    </div>
  );
};

const ToolCard = ({ icon, title, sub, featured = false, onClick }: ToolCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative flex min-h-[110px] flex-col gap-3 overflow-hidden rounded-md p-3.5 text-left transition-transform active:scale-[0.98]",
      featured
        ? "bg-ink text-cream"
        : "border border-line bg-paper text-ink hover:bg-cream-2",
    )}
  >
    {featured && (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-8 -right-8 h-32 w-32 rounded-pill opacity-30"
        style={{ background: "radial-gradient(circle, rgb(var(--wo-mustard)) 0%, transparent 70%)" }}
      />
    )}
    <span
      className={cn(
        "relative grid h-10 w-10 place-items-center rounded-[12px]",
        !featured && "bg-teal/[0.08]",
      )}
    >
      {icon}
    </span>
    <span className="relative">
      <span className={cn("block text-small font-semibold", !featured && "text-ink")}>
        {title}
      </span>
      <span
        className={cn(
          "mt-0.5 block text-meta",
          featured ? "text-cream/70" : "text-muted",
        )}
      >
        {sub}
      </span>
    </span>
  </button>
);
